/* Greenleaf — header interaction. New file, 25 Aug 2026, part of the header
 * redesign (see the long comment at the top of sections/gl-header.liquid for
 * the brief this is built from).
 *
 * FOUR JOBS, kept in one file only because all four are small and all
 * four belong to the same section:
 *
 *   1. THE SEARCH TOGGLE. `.mastsearch__panel` opens on click, closes on an
 *      outside click or Escape, and returns focus to the toggle button on
 *      close. `hidden` is the actual show/hide mechanism (matches the
 *      sticky purchase bar's own pattern on the product page — see
 *      assets/gl-pdp.js — for the same reason: a merely invisible search
 *      field is still in the accessibility tree).
 *
 *   2. PAGE-CONTENT SPACING. `.header-fixed` is `position:fixed` (see the
 *      CSS/Liquid comments for why — `position:sticky` could not stay stuck
 *      past its own ~135px section wrapper on a page thousands of pixels
 *      tall). A fixed element is removed from document flow, so without
 *      this the page's real content would render UNDER it instead of below
 *      it. Measured at runtime and re-measured on resize and on every
 *      compact-state change, rather than hardcoded, because the header's
 *      own height is not constant — it shrinks once scrolled.
 *
 *   3. THE SCROLL-COMPACT STATE. `[data-scrolled]` goes on `.header-fixed`
 *      (not the inner `[data-header]` — `.announce` is `.masthead`'s
 *      SIBLING, both children of `.header-fixed`, so an attribute scoped to
 *      the inner element could never reach it; found live, fixed here)
 *      once the page has scrolled past a small threshold. Unlike the first
 *      version of this file, the threshold is NOT the announcement strip's
 *      height — now that the header is genuinely fixed (always on screen,
 *      not naturally scrolling away with the announce bar above it), the
 *      strip has to be explicitly hidden by this same state instead of
 *      disappearing on its own. A small fixed buffer (24px) avoids the
 *      state flickering on a trackpad's tiny scroll bounce at rest.
 *
 *   4. THE PHONE HEADER STEPS ASIDE (12 Oct 2026, Andrew's yes via the
 *      Coordinator). Below 900px `[data-hidden]` slides the header up while
 *      the page scrolls down, and any scroll up brings it straight back. It
 *      never hides near the top, while the menu or the search is open, or
 *      while focus is inside it. Desktop keeps its shrink and never hides.
 *      `data-header-hidden` on <html> lets things that stick under the
 *      header (the shelf's filter strip) move up into the space.
 *
 * DELIBERATELY NOT requestAnimationFrame-throttled. A plain scroll listener
 * with a short setTimeout debounce was picked instead, specifically because
 * an rAF-driven version stalls completely on a backgrounded/non-visible tab
 * (confirmed the hard way today, debugging an unrelated sticky-bar check —
 * see today's session notes) — that is a real failure mode for a customer
 * who opens this site in a background tab, not just a testing artifact.
 */
(function () {
  'use strict';

  var SCROLL_THRESHOLD = 24;

  function initSearch() {
    var root = document.querySelector('[data-mastsearch]');
    if (!root) return;
    var toggle = root.querySelector('.mastsearch__toggle');
    var panel = root.querySelector('[data-mastsearch-panel]');
    if (!toggle || !panel) return;

    var closeBtn = panel.querySelector('[data-mastsearch-close]');

    function open() {
      panel.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      var input = panel.querySelector('input');
      if (input) input.focus();
    }
    function close(returnFocus) {
      panel.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
      if (returnFocus) toggle.focus();
    }

    toggle.addEventListener('click', function () {
      if (panel.hasAttribute('hidden')) open();
      else close(false);
    });
    if (closeBtn) closeBtn.addEventListener('click', function () { close(true); });
    document.addEventListener('click', function (e) {
      if (panel.hasAttribute('hidden')) return;
      if (root.contains(e.target)) return;
      close(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hasAttribute('hidden')) close(true);
    });
  }

  function initFixedHeader() {
    var wrap = document.querySelector('[data-header-fixed]');
    if (!wrap) return;

    function repad() {
      var h = wrap.getBoundingClientRect().height;
      document.body.style.paddingTop = h + 'px';
      // The phone search panel fills the screen below this height.
      wrap.style.setProperty('--hdr-h', h + 'px');
    }
    repad();
    window.addEventListener('resize', repad, { passive: true });

    var scrolled = false;
    function apply() {
      var shouldBeScrolled = window.scrollY > SCROLL_THRESHOLD;
      if (shouldBeScrolled === scrolled) return;
      scrolled = shouldBeScrolled;
      // On .header-fixed, not header — .announce is .masthead's SIBLING,
      // both children of .header-fixed, so an attribute scoped to
      // [data-header] could never reach .announce no matter what CSS was
      // written against it. Found live: the announce bar stayed visible
      // after scrolling despite [data-scrolled] being set correctly.
      if (scrolled) wrap.setAttribute('data-scrolled', '');
      else wrap.removeAttribute('data-scrolled');
      // The header's own height just changed (padding/logo/labels), so the
      // page's compensating space has to change with it — a moment after
      // the attribute flips, so the new CSS has actually applied first.
      // setTimeout, not requestAnimationFrame, for the same reason the
      // scroll listener above avoids it: this file stays consistent about
      // not depending on rAF anywhere in it.
      setTimeout(repad, 50);
    }
    apply();

    var phone = window.matchMedia('(max-width: 900px)');
    var lastY = window.scrollY;
    var hidden = false;
    function setHidden(on) {
      if (on === hidden) return;
      hidden = on;
      if (on) {
        wrap.setAttribute('data-hidden', '');
        document.documentElement.setAttribute('data-header-hidden', '');
      } else {
        wrap.removeAttribute('data-hidden');
        document.documentElement.removeAttribute('data-header-hidden');
      }
    }
    function busy() {
      var menu = document.querySelector('[data-mmenu]');
      var search = document.querySelector('[data-mastsearch-panel]');
      return (menu && !menu.hasAttribute('hidden')) ||
        (search && !search.hasAttribute('hidden')) ||
        wrap.contains(document.activeElement);
    }
    function autoHide() {
      var y = window.scrollY;
      var dy = y - lastY;
      lastY = y;
      if (!phone.matches || y <= wrap.offsetHeight || busy()) { setHidden(false); return; }
      // 4px of dead band: a finger resting on the glass still reports
      // one-pixel moves, and the header should not twitch on them.
      if (dy > 4) setHidden(true);
      else if (dy < 0) setHidden(false);
    }
    // Opening the menu or the search, or tabbing into the header, shows it.
    wrap.addEventListener('focusin', function () { setHidden(false); });
    if (phone.addEventListener) phone.addEventListener('change', function () { setHidden(false); });

    var pending = null;
    window.addEventListener('scroll', function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        apply();
        autoHide();
      }, 60);
    }, { passive: true });
  }

  function init() {
    initSearch();
    initFixedHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
